import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShoppingBag } from "lucide-react";
import { PageWrap } from "@/components/PageWrap";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { getProducts, resolveImageUrl } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

const ProductCard = ({ product, index }) => {
  const { addItem, setIsOpen } = useCart();
  const { t, lang } = useLang();
  const czk = (n) => (lang === "en" ? `${n.toLocaleString("en-GB")} CZK` : `${n.toLocaleString("cs-CZ")} Kč`);
  const [size, setSize] = useState("");
  const needsSize = product.sizes && product.sizes.length > 0;

  const handleAdd = () => {
    if (needsSize && !size) {
      toast.error(t("shop.sizeFirst"));
      return;
    }
    addItem(product, needsSize ? size : null);
    toast.success(`${product.name} ${t("shop.added")}`, {
      action: { label: t("shop.cartAction"), onClick: () => setIsOpen(true) },
    });
  };

  return (
    <motion.div
      data-testid={`shop-product-card-${product.id}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: 0.06 * (index % 4) }}
      className="clip-corner group flex flex-col border border-white/10 bg-[#0F1217] transition-colors duration-200 hover:border-[#B7FF2A]/35"
    >
      <div className="relative overflow-hidden">
        <img
          src={resolveImageUrl(product.image)}
          alt={product.name}
          className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        {product.badge && (
          <span className="absolute left-3 top-3 bg-[#B7FF2A] px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#07080A]">
            {product.badge}
          </span>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F1217] via-transparent to-transparent opacity-60" />
      </div>
      <div className="flex flex-1 flex-col p-5">
        <div className="font-display text-xl uppercase leading-tight tracking-[0.05em]">{product.name}</div>
        <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-white/45">{product.description}</p>
        <div className="mt-3 font-mono text-lg font-semibold text-[#B7FF2A]">{czk(product.price)}</div>
        <div className="mt-4 flex gap-2">
          {needsSize && (
            <Select value={size} onValueChange={setSize}>
              <SelectTrigger
                data-testid={`product-size-select-${product.id}`}
                className="h-10 w-24 shrink-0 border-white/15 bg-[#0B0D10] font-mono text-sm"
              >
                <SelectValue placeholder={t("shop.sizePh")} />
              </SelectTrigger>
              <SelectContent className="border-white/10 bg-[#0F1217]">
                {product.sizes.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`product-size-option-${product.id}-${s}`}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            data-testid={`product-add-to-cart-button-${product.id}`}
            onClick={handleAdd}
            className="light-sweep clip-corner-sm h-10 flex-1 bg-[#1EA7FF] font-display text-sm uppercase tracking-[0.12em] text-[#07080A] hover:bg-[#1EA7FF] hover:brightness-110"
          >
            <ShoppingBag className="mr-1.5 h-4 w-4" /> {t("shop.addToCart")}
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const { t } = useLang();

  const CATEGORIES = [
    { id: "all", label: t("shop.catAll") },
    { id: "obleceni", label: t("shop.catClothing") },
    { id: "samolepky", label: t("shop.catStickers") },
  ];

  useEffect(() => {
    getProducts()
      .then(setProducts)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = category === "all" ? products : products.filter((p) => p.category === category);

  return (
    <PageWrap testId="shop-page">
      <section className="relative overflow-hidden pt-36 pb-12">
        <div className="hero-glow absolute inset-0" />
        <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B7FF2A]">// {t("shop.kicker")}</div>
            <h1 data-testid="shop-headline" className="mt-2 font-display text-6xl uppercase leading-[0.9] tracking-[0.03em] sm:text-8xl">
              {t("shop.title")}
            </h1>
            <p className="mt-4 max-w-lg text-white/55">
              {t("shop.intro")}
            </p>
          </motion.div>

          {/* category chips */}
          <div className="mt-8 flex flex-wrap gap-2" data-testid="shop-category-filter">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                data-testid={`shop-category-chip-${c.id}`}
                onClick={() => setCategory(c.id)}
                className={`clip-corner-sm px-4 py-2 font-display text-sm uppercase tracking-[0.14em] transition-colors duration-200 ${
                  category === c.id
                    ? "bg-[#B7FF2A] text-[#07080A]"
                    : "border border-white/15 bg-white/[0.03] text-white/60 hover:border-white/35 hover:text-white"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-24 sm:px-6 lg:px-8">
        {loading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="shop-loading">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="clip-corner h-96 animate-pulse border border-white/5 bg-[#0F1217]" />
            ))}
          </div>
        ) : (
          <div data-testid="shop-product-grid" className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {filtered.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="py-16 text-center text-white/40" data-testid="shop-empty-state">
            {t("shop.empty")}
          </div>
        )}
      </section>
    </PageWrap>
  );
}
